/**
 * The `product_selection` view contract (design D20): the assistant proposes
 * products per slot (one slot per requested product kind), the user reviews
 * them in a popup and takes the final selection over with one chat message.
 * Pure helpers: the payload type guard, the per-view state (kept in
 * `productSelectionStore.ts`), the takeover note, how a takeover is found
 * again in the chat. (Its short form in the user's bubble is
 * `productSelectionDisplay.ts`, which needs the Tolgee instance.)
 */
import { productId, type ProductPick } from "./productDisplay.js";
import type { AgentViewSpec } from "./registry.js";

export type ProductSelectionPick = ProductPick & { reason?: string };

export type ProductSelectionFilters = {
  productTypeId?: string;
  /** Set by the app from the search facets; the search narrows by it. */
  productTypeKey?: string;
  brand?: string;
  brandLine?: string;
  supplierId?: string;
};

export type ProductSelectionSlot = {
  key: string;
  label: string;
  search: string;
  filters?: ProductSelectionFilters;
  picks: ProductSelectionPick[];
};

export type ProductSelectionViewSpec = {
  component: "product_selection";
  title: string;
  intent?: string;
  slots: ProductSelectionSlot[];
  /** The history note for the model; never rendered. */
  memory: string;
};

export type ProductSelectionSlotState = {
  /** Products the user added from the search (not among the assistant's picks). */
  added: ProductPick[];
  /** Ids (`productId`) of the checked products. */
  checked: string[];
};

export type TakenOverSlot = {
  key: string;
  label: string;
  products: ProductPick[];
};

export type ProductSelectionState = {
  slots: Record<string, ProductSelectionSlotState>;
  /** Set once the selection was taken over; the view is read-only from then on. */
  takenOver: TakenOverSlot[] | null;
};

// --- payload guard ----------------------------------------------------------

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function asArray(value: unknown): unknown[] | null {
  return Array.isArray(value) ? value : null;
}

function isOptionalString(value: unknown): boolean {
  return value === undefined || typeof value === "string";
}

function isNullableString(value: unknown): boolean {
  return value === undefined || value === null || typeof value === "string";
}

function isPick(value: unknown): boolean {
  if (!isRecord(value)) return false;
  const price = value["price"];
  return (
    typeof value["articleNumber"] === "string" &&
    typeof value["supplierId"] === "string" &&
    typeof value["title"] === "string" &&
    isNullableString(value["brand"]) &&
    isNullableString(value["imageUrl"]) &&
    (price === undefined ||
      price === null ||
      (isRecord(price) &&
        typeof price["amount"] === "number" &&
        typeof price["currency"] === "string")) &&
    isOptionalString(value["reason"])
  );
}

function isFilters(value: unknown): boolean {
  return (
    value === undefined ||
    (isRecord(value) &&
      isOptionalString(value["productTypeId"]) &&
      isOptionalString(value["productTypeKey"]) &&
      isOptionalString(value["brand"]) &&
      isOptionalString(value["brandLine"]) &&
      isOptionalString(value["supplierId"]))
  );
}

function isSlot(value: unknown): boolean {
  if (!isRecord(value)) return false;
  const picks = asArray(value["picks"]);
  return (
    typeof value["key"] === "string" &&
    value["key"] !== "" &&
    typeof value["label"] === "string" &&
    typeof value["search"] === "string" &&
    isFilters(value["filters"]) &&
    picks !== null &&
    picks.every(isPick)
  );
}

export function isProductSelectionView(
  view: AgentViewSpec
): view is ProductSelectionViewSpec {
  const slots = asArray(view.slots);
  return (
    view.component === "product_selection" &&
    typeof view.title === "string" &&
    isOptionalString(view.intent) &&
    typeof view.memory === "string" &&
    slots !== null &&
    slots.length > 0 &&
    slots.every(isSlot) &&
    // Slot keys identify the slot's state: they must be unique.
    new Set(slots.map(slot => (isRecord(slot) ? slot["key"] : slot))).size ===
      slots.length
  );
}

// --- state ------------------------------------------------------------------

/** What identifies a view in the store: its title and what the assistant remembers of it. */
export function productSelectionKey(view: ProductSelectionViewSpec): string {
  return `${view.title}\u0000${view.memory}`;
}

/** Initially every pick of the assistant is checked, nothing added. */
export function initialProductSelectionState(
  view: ProductSelectionViewSpec
): ProductSelectionState {
  return {
    slots: Object.fromEntries(
      view.slots.map(slot => [
        slot.key,
        { added: [], checked: slot.picks.map(productId) },
      ])
    ),
    takenOver: null,
  };
}

function slotState(
  state: ProductSelectionState,
  slot: ProductSelectionSlot
): ProductSelectionSlotState {
  return (
    state.slots[slot.key] ?? { added: [], checked: slot.picks.map(productId) }
  );
}

/** The slot's products in display order: the assistant's picks, then the user's additions. */
export function slotProducts(
  slot: ProductSelectionSlot,
  state: ProductSelectionState
): ProductSelectionPick[] {
  return [...slot.picks, ...slotState(state, slot).added];
}

type ProductIdentity = Pick<ProductPick, "articleNumber" | "supplierId">;

export function isChecked(
  slot: ProductSelectionSlot,
  state: ProductSelectionState,
  product: ProductIdentity
): boolean {
  return slotState(state, slot).checked.includes(productId(product));
}

export function checkedProducts(
  slot: ProductSelectionSlot,
  state: ProductSelectionState
): ProductSelectionPick[] {
  return slotProducts(slot, state).filter(product =>
    isChecked(slot, state, product)
  );
}

/**
 * The most products of one slot a takeover note may carry — the backend's
 * takeover schema refuses more, and a refused note would read as plain text.
 * Checking or adding beyond it is a no-op.
 */
export const MAX_TAKEOVER_PRODUCTS_PER_SLOT = 50;

export function toggleProduct(
  state: ProductSelectionState,
  slot: ProductSelectionSlot,
  product: ProductIdentity
): ProductSelectionState {
  const current = slotState(state, slot);
  const id = productId(product);
  if (
    !current.checked.includes(id) &&
    current.checked.length >= MAX_TAKEOVER_PRODUCTS_PER_SLOT
  )
    return state;
  const checked = current.checked.includes(id)
    ? current.checked.filter(c => c !== id)
    : [...current.checked, id];
  return {
    ...state,
    slots: { ...state.slots, [slot.key]: { ...current, checked } },
  };
}

/**
 * Takes a product out of the slot's selection: an assistant pick is unchecked
 * (the card shows it unchecked), a product the user added is dropped.
 */
export function removeProduct(
  state: ProductSelectionState,
  slot: ProductSelectionSlot,
  product: ProductIdentity
): ProductSelectionState {
  const current = slotState(state, slot);
  const id = productId(product);
  return {
    ...state,
    slots: {
      ...state.slots,
      [slot.key]: {
        added: current.added.filter(p => productId(p) !== id),
        checked: current.checked.filter(c => c !== id),
      },
    },
  };
}

/** Moves a searched product into the slot's list, checked. Already listed products are just checked. */
export function addProduct(
  state: ProductSelectionState,
  slot: ProductSelectionSlot,
  product: ProductPick
): ProductSelectionState {
  const current = slotState(state, slot);
  const id = productId(product);
  if (
    !current.checked.includes(id) &&
    current.checked.length >= MAX_TAKEOVER_PRODUCTS_PER_SLOT
  )
    return state;
  const listed = slotProducts(slot, state).some(p => productId(p) === id);
  return {
    ...state,
    slots: {
      ...state.slots,
      [slot.key]: {
        added: listed ? current.added : [...current.added, product],
        checked: current.checked.includes(id)
          ? current.checked
          : [...current.checked, id],
      },
    },
  };
}

/**
 * A taken-over product with the display data (image, brand, price) it had in
 * the view: the assistant's pick from the spec, or a product the user added
 * from the search (kept in the store). The takeover note carries none.
 */
export function withDisplayData(
  view: ProductSelectionViewSpec,
  state: ProductSelectionState,
  slotKey: string,
  product: ProductPick
): ProductPick {
  const id = productId(product);
  const known =
    view.slots.flatMap(slot => slot.picks).find(p => productId(p) === id) ??
    (state.slots[slotKey]?.added ?? []).find(p => productId(p) === id);
  // Known products get explicit nulls for absent fields, so only a product the
  // view never had (undefined fields) is looked up in the catalogue.
  return known
    ? {
        ...product,
        ...known,
        title: product.title,
        brand: known.brand ?? null,
        imageUrl: known.imageUrl ?? null,
        price: known.price ?? null,
      }
    : product;
}

export function selectedCount(
  view: ProductSelectionViewSpec,
  state: ProductSelectionState
): number {
  return view.slots.reduce(
    (sum, slot) => sum + checkedProducts(slot, state).length,
    0
  );
}

export function takeOverSlots(
  view: ProductSelectionViewSpec,
  state: ProductSelectionState
): TakenOverSlot[] {
  return view.slots.map(slot => ({
    key: slot.key,
    label: slot.label,
    products: checkedProducts(slot, state),
  }));
}

// --- takeover note ----------------------------------------------------------

// The note is the wire format the backend parses (`parseProductSelectionNote`);
// the German wording is part of that contract, not user-visible copy.
export const PRODUCT_SELECTION_NOTE_PREFIX = "[Produktauswahl übernommen: ";

/** The user message that hands the final selection back to the assistant: quantity 1 per chosen product. */
export function buildProductSelectionNote(
  title: string,
  slots: TakenOverSlot[]
): string {
  const payload = {
    title,
    slots: slots.map(slot => ({
      key: slot.key,
      label: slot.label,
      products: slot.products.map(product => ({
        articleNumber: product.articleNumber,
        supplierId: product.supplierId,
        title: product.title,
        quantity: 1,
      })),
    })),
  };
  return `${PRODUCT_SELECTION_NOTE_PREFIX}${JSON.stringify(payload)}]`;
}

function parseTakenOverProduct(value: unknown): ProductPick | null {
  if (
    !isRecord(value) ||
    typeof value["articleNumber"] !== "string" ||
    typeof value["supplierId"] !== "string" ||
    typeof value["title"] !== "string"
  )
    return null;
  return {
    articleNumber: value["articleNumber"],
    supplierId: value["supplierId"],
    title: value["title"],
  };
}

function parseTakenOverSlot(value: unknown): TakenOverSlot | null {
  if (
    !isRecord(value) ||
    typeof value["key"] !== "string" ||
    typeof value["label"] !== "string"
  )
    return null;
  const rawProducts = asArray(value["products"]);
  if (!rawProducts) return null;
  const products: ProductPick[] = [];
  for (const raw of rawProducts) {
    const product = parseTakenOverProduct(raw);
    if (!product) return null;
    products.push(product);
  }
  return { key: value["key"], label: value["label"], products };
}

/** The note's title and slots, or `null` when the text is not exactly a well-formed note. */
export function parseProductSelectionNote(
  text: string
): { title: string; slots: TakenOverSlot[] } | null {
  const trimmed = text.trim();
  if (
    !trimmed.startsWith(PRODUCT_SELECTION_NOTE_PREFIX) ||
    !trimmed.endsWith("]")
  )
    return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(
      trimmed.slice(PRODUCT_SELECTION_NOTE_PREFIX.length, -1)
    );
  } catch {
    return null;
  }
  if (!isRecord(parsed) || typeof parsed["title"] !== "string") return null;
  const rawSlots = asArray(parsed["slots"]);
  if (!rawSlots) return null;
  const slots: TakenOverSlot[] = [];
  for (const raw of rawSlots) {
    const slot = parseTakenOverSlot(raw);
    if (!slot) return null;
    slots.push(slot);
  }
  return { title: parsed["title"], slots };
}

const keysOf = (slots: Array<{ key: string }>): string =>
  slots
    .map(slot => slot.key)
    .sort()
    .join("|");

/**
 * The final products of the takeover note in the user message that followed
 * this view (`answeredWith`), or `null` when that message is not one. A note
 * belongs to the view when its title and its set of slot keys match.
 */
export function findTakenOverSlots(
  view: ProductSelectionViewSpec,
  answeredWith: string | undefined
): TakenOverSlot[] | null {
  if (answeredWith === undefined) return null;
  const note = parseProductSelectionNote(answeredWith);
  return note &&
    note.title === view.title &&
    keysOf(note.slots) === keysOf(view.slots)
    ? note.slots
    : null;
}
